'use client';

import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, X, Circle, Sparkles, Loader2 } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { useTourAI } from './hooks/useTourAI';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { TourStep } from '@/config/guided-tours/types';

interface GuidedTourQuickGuideProps {
  onComplete?: () => void;
}

export function GuidedTourQuickGuide({ onComplete }: GuidedTourQuickGuideProps) {
  const { isOpen, engine, progress, selectedFlow } = useGuidedTour();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [aiMarkdown, setAiMarkdown] = useState<string | null>(null);
  const tourAI = useTourAI({ enabled: isOpen && engine.viewMode === 'runner' });

  const steps = useMemo<TourStep[]>(() => selectedFlow?.steps ?? [], [selectedFlow?.steps]);
  const allChecked = steps.length > 0 && steps.every((step) => checkedIds.includes(step.id));
  const aiGenerateEnabled = !!selectedFlow?.quick_guide?.ai_generate;

  useEffect(() => {
    if (allChecked && steps[0]?.id) {
      progress.markStepVerified(steps[0].id);
      onComplete?.();
    }
  }, [allChecked, onComplete, progress, steps]);

  // Fetch AI-generated quick guide markdown when ai_generate is enabled
  useEffect(() => {
    if (!isOpen || !aiGenerateEnabled || !selectedFlow || !tourAI.aiAvailable) return;
    let cancelled = false;
    tourAI.generateQuickGuide(selectedFlow).then((md) => {
      if (!cancelled && md) setAiMarkdown(md);
    });
    return () => { cancelled = true; };
  }, [isOpen, aiGenerateEnabled, selectedFlow?.id, tourAI.aiAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || engine.viewMode !== 'runner' || engine.currentFormat !== 'quick-guide' || engine.isMinimized) {
    return null;
  }

  const toggleCheck = (id: string) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((checkedId) => checkedId !== id) : [...prev, id]
    );
  };

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,28rem)]">
      <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
        <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
          <div>
            <CardTitle className="text-base">{selectedFlow?.label}</CardTitle>
            <p className="text-muted-foreground text-xs">
              Check off each step as you complete it.
            </p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={engine.minimize} title="Minimize tour">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="max-h-[60vh] space-y-2 overflow-y-auto pt-0">
          {/* AI-generated quick guide markdown */}
          {aiGenerateEnabled && tourAI.quickGuideLoading && (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI is generating your quick guide...</span>
            </div>
          )}
          {aiGenerateEnabled && aiMarkdown && !tourAI.quickGuideLoading && (
            <div className="prose prose-sm prose-slate max-w-none rounded-lg border border-indigo-100 bg-indigo-50/30 p-3 dark:border-indigo-800/60 dark:bg-indigo-900/20">
              <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                <Sparkles className="h-3 w-3" /> AI-Generated Guide
              </div>
              <div className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{aiMarkdown}</div>
            </div>
          )}

          <div className="prose prose-sm prose-slate max-w-none">
            {steps.map((step) => {
              const isChecked = checkedIds.includes(step.id);
              return (
                <button
                  key={step.id}
                  onClick={() => toggleCheck(step.id)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                    isChecked
                      ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800/60 dark:bg-emerald-900/20'
                      : 'border-slate-100 bg-slate-50/60 hover:bg-slate-100/60 dark:border-slate-700/80 dark:bg-slate-800/60 dark:hover:bg-slate-700/60'
                  )}
                >
                  <span className="mt-0.5 shrink-0">
                    {isChecked ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Circle className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                    )}
                  </span>
                  <span className={cn('flex-1 text-sm', isChecked && 'text-slate-500 dark:text-slate-400 line-through')}>
                    <span className="font-semibold dark:text-slate-100">{step.title}</span>
                    {step.summary && (
                      <span className="ml-1 text-slate-500 dark:text-slate-400">— {step.summary}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => engine.setCurrentFormat('interactive')}
            >
              Switch to interactive
            </Button>

            <Button size="sm" disabled={!allChecked} onClick={engine.goToNextStep}>
              {engine.isLastStep ? 'Finish' : 'Continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
